import { z } from "zod";

export const EXPORT_CONTRACT_VERSION = 2 as const;

export const EXPORT_CAPABILITIES = [
  "structured-abstract-v1",
  "editable-word-tables-v1",
  "figure-legends-v1",
  "text-emphasis-rules-v1",
  "content-fidelity-gate-v1",
  "submission-readiness-v1",
] as const;

const apiPathSchema = z
  .string()
  .max(2000)
  .refine((value) => value === "" || value.startsWith("/api/exports/"));

const healthSchema = z
  .object({
    ok: z.boolean(),
    exportContractVersion: z.literal(EXPORT_CONTRACT_VERSION),
    capabilities: z.array(z.string()).max(100),
  })
  .passthrough()
  .superRefine((value, context) => {
    for (const capability of EXPORT_CAPABILITIES) {
      if (!value.capabilities.includes(capability)) {
        context.addIssue({
          code: "custom",
          path: ["capabilities"],
          message: `missing required capability ${capability}`,
        });
      }
    }
  });

const exportResponseSchema = z
  .object({
    exportContractVersion: z.literal(EXPORT_CONTRACT_VERSION),
    id: z.uuid(),
    status: z.enum(["passed", "warnings", "blocked"]),
    downloadUrl: apiPathSchema.refine((value) => value.endsWith("/download")),
    audit: z.object({
      summary: z.string(),
      deterministic: z.string(),
      postflight: z.string(),
      contentFidelity: z.string(),
      realRender: z.string(),
    }),
    delivery: z.object({
      state: z.enum(["submission_ready", "review_required", "internal_draft"]),
      label: z.string(),
      downloadName: z.string(),
      disclaimer: z.string(),
    }),
    blockingReasons: z.array(z.string()),
    warningCount: z.number().int().nonnegative(),
    contentWarnings: z.array(z.string()),
    auditWarnings: z.array(z.string()),
    reportUrls: z.object({
      repair: apiPathSchema,
      deterministic: apiPathSchema,
      postflight: apiPathSchema,
      visual: apiPathSchema,
      contentFidelity: apiPathSchema,
    }),
  })
  .superRefine((value, context) => {
    const expected = {
      passed: {
        state: "submission_ready",
        filename: "manuscript_submission_ready.docx",
      },
      warnings: {
        state: "review_required",
        filename: "manuscript_review_required.docx",
      },
      blocked: {
        state: "internal_draft",
        filename: "manuscript_internal_draft.docx",
      },
    } as const;
    const contract = expected[value.status];
    if (value.delivery.state !== contract.state) {
      context.addIssue({
        code: "custom",
        path: ["delivery", "state"],
        message: "delivery state does not match pipeline status",
      });
    }
    if (value.delivery.downloadName !== contract.filename) {
      context.addIssue({
        code: "custom",
        path: ["delivery", "downloadName"],
        message: "download filename does not match delivery state",
      });
    }
    if (
      value.warningCount !==
      value.contentWarnings.length + value.auditWarnings.length
    ) {
      context.addIssue({
        code: "custom",
        path: ["warningCount"],
        message: "warning count does not match warning arrays",
      });
    }
    if (value.status === "passed" && value.warningCount !== 0) {
      context.addIssue({
        code: "custom",
        path: ["status"],
        message: "passed export contains unresolved warnings",
      });
    }
  });

const apiErrorSchema = z.object({ error: z.string().trim().min(1).max(3000) });

export type ExportHealth = z.infer<typeof healthSchema>;
export type ExportResponse = z.infer<typeof exportResponseSchema>;

export function parseExportHealthResponse(value: unknown): ExportHealth {
  return healthSchema.parse(value);
}

export function parseExportResponse(value: unknown): ExportResponse {
  return exportResponseSchema.parse(value);
}

export function readApiError(value: unknown, fallback: string): string {
  const parsed = apiErrorSchema.safeParse(value);
  return parsed.success ? parsed.data.error : fallback;
}
